import AddIcon from '@mui/icons-material/Add';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Grid, IconButton, styled, Switch, Stack, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IOrganizationalUnit,
  fetchDashboardCollectionsAssignedToOrganisationalUnitAccountManagers,
  fetchDashboardCollectionsAssignedToOrganisationalUnitTeams,
  getAllOrganizationalUnitSettings,
  getAllOrganizationalUnits,
  getMyPinnedDashboardsCollection,
} from 'wisestep-api';
import {
  WsButton,
  getDateUsingFormat,
  WsTypography,
  WsTypoVariants,
  EmptyTeamsAndUsersIcon,
  Item,
  WsCard,
  CardVariant,
  WsColors,
  WsTypoColors,
  SearchBarNew,
} from 'wisestep-base';

import PinDashboardCollectionButton, {
  DashboardCollectionIdType,
} from '../../../components/pinDashboardCollectionButton/PinDashboardCollectionButton';
import { WsBreadCrumbs } from '../../../components/WsBreadCrumbs';
import WsContextMenuHandler from '../../../components/WsContextMenu/WsContextMenuHandler';
import { useWorkspaceContext } from '../../../context';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import OrgUnit from '../../../images/Avatar/OrgUnit.svg';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import Loader from '../../conversationWidget/components/Loader';
import WsDataGridPagination from '../../submissionsNew/components/widgets/WsDataGridPagination';
import { StyledDataGrid } from '../../teams/components/TeamListNew';
import WsAvatar from '../../teams/components/WsAvatar';
import { ScopeType } from '../helper/ClientSettingsHelper';

import { countsMap } from './OrganizationalUnitSettings';

export interface ILabelWithStatus {
  label: string;
  labelTwo?: string;
  status: boolean;
}

export const HeaderBox = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'row',
  minWidth: '100%',
}));

export const StyledSwitch = styled(Switch)(() => ({
  top: '0.5vw',
}));

export const StyledPaginationBox = styled(Box)(() => ({
  display: 'flex',
  height: 60,
  backgroundColor: WsColors.White,
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${WsColors.Gray88}`,
}));

interface IClientSettingsLayout {
  title: string;
  headerList: string[];
  organizationalUnits?: IOrganizationalUnit[];
  itemsList?: ILabelWithStatus[];
  dataCallback?(data: boolean, index: number): void;
  handleAddButton?: () => void;
  addButtonText?: string;
  backUrl: string;
  loader?: boolean;
  getOrganizationalUnit?: (index: number) => void;
  readonlyMode?: boolean;
  teamCount?: countsMap;
  accountManagerCount?: countsMap;
  scopeType: string | undefined;
}
interface ITableState {
  organizationalUnitName: string;
  index: number;
  createdOn: string | undefined;
  owner: string | undefined;
  id: number | string;
  scopeType: string | undefined;
}

const OrganizationalUnitLayout = ({
  addButtonText,
  handleAddButton,
  title,
  loader,
  getOrganizationalUnit,
  readonlyMode,
  teamCount,
  accountManagerCount,
  scopeType,
}: IClientSettingsLayout) => {
  const navigate = useNavigate();
  const location = useLocation();
  const workspace = useWorkspaceContext();
  const isAdmin = workspace.owns;
  const path = location.pathname;
  const methods = useForm();
  const { watch, setValue } = methods;
  const isTeamsOrganizationalUnit: boolean =
    path == '/workspace-users-and-teams/organizational-unit-recruitment' ? true : false;
  const isManagerOrganizationalUnit: boolean =
    path == '/workspace-users-and-teams/account-manager-units' ? true : false;

  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [dashboardObj, setDashboardObj] = useState<DashboardCollectionIdType>();
  const [tableData, setTableData] = useState<ITableState[]>([]);
  const [pinnedDashboardCheck, setPinnedDashboardCheck] = useState([]);
  const [organizationalUnits, setOrganizationalUnits] = useState<IOrganizationalUnit[]>([]);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const getData = useCallback(() => {
    return (
      organizationalUnits?.map((item, index) => ({
        organizationalUnitName: item.name ?? '',
        index: index,
        createdOn: item.createdOn
          ? getDateUsingFormat(item.createdOn.toString(), 'DD MMM YYYY')
          : '',
        owner: item.createdBy ?? '',
        id: item.id ?? index,
        scopeType: item.scopeType ?? '',
      })) || []
    );
  }, [organizationalUnits]);

  const fetchData = useCallback(async () => {
    const response = scopeType
      ? await getAllOrganizationalUnits(scopeType)
      : await getAllOrganizationalUnitSettings();
    const uniqueUnits = response.data.filter(
      (unit, index, self) => index === self.findIndex((item) => item.id === unit.id)
    );
    setOrganizationalUnits(uniqueUnits);
  }, [scopeType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (path) {
      setTableData(getData());
    }
  }, [path, getData]);

  useEffect(() => {
    const dashboardAssignedIds = async () => {
      try {
        const response = isTeamsOrganizationalUnit
          ? await fetchDashboardCollectionsAssignedToOrganisationalUnitTeams(true)
          : await fetchDashboardCollectionsAssignedToOrganisationalUnitAccountManagers(true);
        const ids = response.data?.map((item) => item.id);
        setDashboardObj({
          dashboardIds: ids,
          type: isTeamsOrganizationalUnit ? 'DeliveryUnit' : 'AccountManagerUnit',
        });
      } catch (error) {
        setNotification(failed(`Unable to get  assigned Dashboards`));
      }
    };
    const dashboardInPinnedList = async () => {
      try {
        const responseCollection = await getMyPinnedDashboardsCollection();
        if (responseCollection.data?.length > 0)
          setPinnedDashboardCheck(responseCollection.data?.map((item) => item?.entityId));
      } catch (error) {
        setNotification(failed(`Unable to get Pinned Dashboard Data`));
      }
    };
    dashboardAssignedIds();
    dashboardInPinnedList();
  }, [isTeamsOrganizationalUnit, setNotification]);

  useEffect(() => {
    if (organizationalUnits) {
      const updatedTableData = getData();
      setTableData(updatedTableData);
    }
  }, [organizationalUnits, getData]);

  const onEdit = (index: number) => {
    getOrganizationalUnit && getOrganizationalUnit(index);
  };

  const onDrillDown = (index: number, isOpenNewTab = false) => {
    const url = '/organizational_unit/user-view';
    const state = {
      organizationalUnitDescription: organizationalUnits?.[index].description,
      organizationalUnitName: organizationalUnits?.[index].name,
      organizationalUnitId: organizationalUnits?.[index].id,
      organizationalUnitScopeType: organizationalUnits?.[index].scopeType,
      isTeamsOrganizationalUnit: isTeamsOrganizationalUnit,
      isManagerOrganizationalUnit: isManagerOrganizationalUnit,
      isSettingsPage: !readonlyMode,
    };
    isOpenNewTab
      ? openUrlInNewTab(`${url}?state=${getEncodedQueryParams(state)}`)
      : navigate(url, {
          state: state,
        });
  };
  const index = useRef(0);
  const defaultContextMenuValues = { showContext: false, x: 0, y: 0 };
  const [contextMenuDetails, setContextMenuDetails] = useState(defaultContextMenuValues);
  const menuItems = [
    {
      name: 'Open in new tab',
      onclick: () => onDrillDown(index.current, true),
    },
  ];

  const handleContextMenu = (e, indexValue) => {
    index.current = indexValue;
    e.preventDefault();
    setContextMenuDetails({
      showContext: true,
      x: e.pageX,
      y: e.pageY,
    });
  };
  const enableAddback = isAdmin && !readonlyMode && addButtonText && !isEmpty(organizationalUnits);
  const columns: GridColDef[] = [
    ...(enableAddback
      ? []
      : [
          {
            field: 'PinUnpin',
            headerName: '',
            width: 50,
            hideSortIcons: true,
            renderCell: (params) => {
              if (dashboardObj?.dashboardIds?.length) {
                return (
                  <PinDashboardCollectionButton
                    filters={null}
                    entityId={params.row?.id}
                    entityName={params.row?.organizationalUnitName}
                    homeUnpin={true}
                    setCollect={true}
                    dashbaordsCollectionIds={dashboardObj}
                    isPinned={countMatchingItems(params) === dashboardObj?.dashboardIds?.length}
                  />
                );
              }
            },
          },
        ]),
    {
      field: 'organizationalUnitName',
      headerName: scopeType == ScopeType.Teams ? 'Delivery Unit Name' : 'Account Manager Unit Name',
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <WsAvatar src={OrgUnit} size="small" variant="rounded" />
          </Grid>
          <Grid item>
            <WsTypography
              cursor="pointer"
              color={WsTypoColors.PRIMARY750}
              variant={WsTypoVariants.SUB_TITLE}
              onClick={() => onDrillDown(params.row.index)}
              onContextMenu={(e) => handleContextMenu(e, params.row.index)}
            >
              {params.value}
            </WsTypography>
          </Grid>
        </Grid>
      ),
    },
    ...(readonlyMode
      ? [
          {
            field: 'id',
            headerName: isTeamsOrganizationalUnit ? 'Teams' : 'Account Managers',
            sortable: false,
            flex: 1,
            renderCell: (params) => (
              <Grid container direction="row" sx={{ cursor: 'pointer' }} spacing={1}>
                <Grid item>
                  <WsTypography>
                    {isTeamsOrganizationalUnit
                      ? teamCount?.[params.value] ?? '0'
                      : accountManagerCount?.[params.value] ?? '0'}
                  </WsTypography>
                </Grid>
              </Grid>
            ),
          },
        ]
      : []),
    {
      field: 'createdOn',
      headerName: 'Date Created',
      sortable: false,
      flex: 1,
      renderCell: (params) => <WsTypography>{params.value}</WsTypography>,
    },
    ...(!readonlyMode
      ? [
          {
            field: 'owner',
            headerName: 'Owner',
            sortable: false,
            flex: 1,
          },
          {
            field: 'scopeType',
            headerName: 'Scope Type',
            sortable: false,
            flex: 1,
            renderCell: (params) => (
              <Grid container direction="row" sx={{ cursor: 'pointer' }} spacing={1}>
                <Grid item>
                  <WsTypography bold={true} variant={WsTypoVariants.BODY1}>
                    {params.value}
                  </WsTypography>
                </Grid>
              </Grid>
            ),
          },
        ]
      : []),
  ];

  if (isAdmin && !readonlyMode) {
    columns.push({
      field: 'id',
      headerName: '',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Grid item>
          <IconButton onClick={() => onEdit(params.row.index)}>
            <BorderColorIcon />
          </IconButton>
        </Grid>
      ),
    });
  }

  const countMatchingItems = (params) =>
    pinnedDashboardCheck?.filter((item) => item === params.row?.id).length;

  const resetTofetch = (returnFlag) => {
    const data = getData();
    setTableData(data);
    if (returnFlag) return data;
  };
  const onSearch = () => {
    const query = watch('query');
    const originalData = resetTofetch(true);
    if (query) {
      const filteredData = originalData?.filter((item) =>
        item.organizationalUnitName.toLowerCase().includes(query.toLowerCase())
      );
      filteredData && setTableData(filteredData);
      setPageNo(0);
    } else {
      clear();
    }
  };

  const clear = () => {
    setValue('query', '');
    resetTofetch(false);
  };

  const paginationComponent = (
    <StyledPaginationBox>
      <Box ml={2} display="flex" gap={1}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>Total Count:</WsTypography>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
          {tableData?.length ?? 0}
        </WsTypography>
      </Box>
      {tableData && tableData.length > 10 && (
        <WsDataGridPagination
          count={Math.ceil(tableData.length / pageSize)}
          page={pageNo + 1}
          onPageChange={(_$, newPage) => setPageNo(newPage - 1)}
          pageSize={pageSize}
          onPageSizeChange={({ target: { value } }) => setPageSize(value)}
        />
      )}
    </StyledPaginationBox>
  );

  return (
    <Grid
      container
      bgcolor={WsColors.White}
      sx={{ boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)', borderRadius: 2, p: 2 }}
      overflow="hidden"
    >
      <Grid item xs={12} pt={1} pr={1}>
        {isAdmin && !readonlyMode && addButtonText && !isEmpty(organizationalUnits) && (
          <WsBreadCrumbs
            breadCrumbsList={getSettingBreadCrumbList(
              ISettingBreadCrumbTypes.TEAMS_AND_USERS,
              Pages['ORGANIZATIONAL_UNITS']
            )}
          />
        )}
      </Grid>
      <Grid container item xs={12}>
        <Grid item xs={8} ml={2} display="flex" gap={1.5} alignItems="center">
          <WsTypography variant={WsTypoVariants.TITLE} bold={true}>
            {title}
          </WsTypography>
          <Tooltip title={`View ${title} details here`}>
            <IconButton sx={{ p: 0 }}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Grid>
        {contextMenuDetails.showContext && (
          <WsContextMenuHandler
            top={contextMenuDetails.y}
            left={contextMenuDetails.x}
            menuItems={menuItems}
            setContextMenuDetails={setContextMenuDetails}
          />
        )}
        {organizationalUnits && organizationalUnits.length > 0 && !loader ? (
          <>
            {isAdmin && !readonlyMode && (
              <Grid item xs={12} justifyItems="flex-end" sx={{ display: 'flex' }} gap={1}>
                {addButtonText && !isEmpty(organizationalUnits) && (
                  <WsButton
                    startIcon={<AddIcon />}
                    variant="contained"
                    onClick={handleAddButton}
                  >{`Add ${addButtonText}`}</WsButton>
                )}
              </Grid>
            )}
            {!enableAddback && (
              <Grid item xs={5} sx={{ my: 2, ml: 2 }}>
                <FormProvider {...methods}>
                  <SearchBarNew size="small" onSearch={onSearch} name="query" />
                </FormProvider>
              </Grid>
            )}
            <Grid item xs={12}>
              {paginationComponent}
              <StyledDataGrid
                autoHeight
                hideFooter
                columns={columns}
                rows={tableData ?? []}
                page={pageNo}
                pageSize={pageSize}
              />
              {tableData && tableData.length > 10 && paginationComponent}
            </Grid>
          </>
        ) : loader ? (
          <Box textAlign="center" pt={15} px={4}>
            <Loader />
          </Box>
        ) : (
          <WsCard
            title=""
            sx={{ mt: 4, width: '100%', boxShadow: 'none' }}
            isButton={false}
            cardType={CardVariant.WITH_LIGHT_BG}
            middleComponent={
              <Item>
                <Stack alignItems="center" spacing={3}>
                  <EmptyTeamsAndUsersIcon />
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>Its lonely here</WsTypography>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                    No Oraganization units created in the workspace
                  </WsTypography>
                  <Box p={2}>
                    {isAdmin && !readonlyMode && (
                      <WsButton
                        startIcon={<AddIcon />}
                        variant="outlined"
                        onClick={handleAddButton}
                      >{`Add ${addButtonText}`}</WsButton>
                    )}
                  </Box>
                </Stack>
              </Item>
            }
          />
        )}
      </Grid>
    </Grid>
  );
};

export default OrganizationalUnitLayout;
